import React, { useEffect, useState } from "react";
import { getMyProfile, updateMyProfile } from "../../services/profileService";
import toast from "react-hot-toast";
import Button from "../../components/Button";
import InputField from "../../components/InputField";
import { useAuth } from "../../context/AuthContext";
import Modal from "../../components/Modal";
import Logo from '../../assets/images/Logo.png';

type MyProfile = {
  login_id: number;
  username: string;
  role_id: number;
  email: string;
  mobile_number: string;
  role: string;
  status: string;
  signup_date: string;
  created_at: string;
  updated_at: string;
};

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isValidMobile = (mobile: string) => {
  const digits = mobile.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

const Profile: React.FC = () => {
  const [myprofile, setMyprofile] = useState<MyProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const isCustomer = user?.role === "Customer";

  // confirmation modal open state
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [form, setForm] = useState({
    username: "",
    email: "",
    mobile_number: "",
    password: "",
  });

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await getMyProfile();
      setMyprofile(data);
      setForm({
        username: data.username ?? "",
        email: data.email ?? "",
        mobile_number: data.mobile_number ?? "",
        password: "",
      });
    } catch (err: any) {
      console.error("Load profile failed:", err);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const startEdit = () => {
    if (!myprofile) return;
    setForm({
      username: myprofile.username,
      email: myprofile.email,
      mobile_number: myprofile.mobile_number,
      password: "",
    });
    setFieldError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    if (!myprofile) return;
    setEditing(false);
    setFieldError(null);
    setForm((prev) => ({ ...prev, password: "" }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!form.username.trim()) return "Username is required.";
    if (!isValidEmail(form.email)) return "Invalid email address.";
    if (!isValidMobile(form.mobile_number)) return "Invalid mobile number.";
    if (form.password && form.password.length < 6)
      return "Password must be at least 6 characters.";
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setFieldError(v);
      return;
    }
    setFieldError(null);
    setSaving(true);

    try {
      const payload: any = {
        username: form.username,
        email: form.email,
        mobile_number: form.mobile_number,
      };
      if (form.password.trim() !== "") payload.password = form.password;

      const updated = await updateMyProfile(payload);

      // success toast
      toast.success("Profile updated");

      if (updated) {
        if (
          updated.username ||
          updated.email ||
          updated.mobile_number ||
          updated.login_id
        ) {
          setMyprofile((prev) => ({ ...(prev as MyProfile), ...updated }));
        } else {
          await loadProfile();
        }
      } else {
        await loadProfile();
      }

      setEditing(false);
      setForm((prev) => ({ ...prev, password: "" }));
    } catch (err: any) {
      console.error("Update failed:", err);
      const msg =
        err?.response?.data?.message || err?.message || "Update failed";
      toast.error(msg);
      setFieldError(msg);
    } finally {
      setSaving(false);
      // make sure modal is closed after the attempt
      setConfirmOpen(false);
    }
  };

  // called by the UI to request confirmation before saving
  const requestSaveConfirmation = () => {
    setFieldError(null);
    setConfirmOpen(true);
  };
const formatDateTime = (dateStr: string) => {
  if (!dateStr) return "";

  const date = new Date(dateStr.replace(" ", "T")); // important fix

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12; // convert to 12-hour format

  return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
};

  if (loading) {
    return (
      <div className="pt-3 ">
        <div className="animate-pulse p-6 bg-white rounded-xl shadow-md">
          <div className="h-6 bg-gray-200 rounded w-1/3 md:w-1/4 mb-4"></div>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-3 bg-gray-200 rounded mb-2 w-1/2 md:w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded  w-3/4 md:w-2/4"></div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full md:w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-full md:w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-full md:w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!myprofile) return <div className="p-6">Profile not found.</div>;

  return (
    <div className="pt-3">
      <div className="p-6 bg-white rounded-xl shadow-md">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold">Profile Info</h2>

          {isAdmin && (
            <div>
              {!editing ? (
                <Button variant="primary" onClick={startEdit}>
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  {/* now opens confirmation modal instead of directly saving */}
                  <Button
                    onClick={requestSaveConfirmation}
                    disabled={saving}
                    variant="primary"
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    onClick={cancelEdit}
                    disabled={saving}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 mb-4">
          <img
            src={Logo}
            alt="avatar"
            className="w-16 h-16 "
          />
          <div>
            <div className="font-semibold">{myprofile.username}</div>
            <div className="text-gray-500 text-sm">{myprofile.email}</div>
          </div>
        </div>

        {fieldError && (
          <div className="mb-3 text-sm text-red-600">{fieldError}</div>
        )}

        {!editing ? (
          <div className="space-y-1">
            <div>
              <strong>Role:</strong> {myprofile.role}
            </div>
            <div>
              <strong>Status:</strong> {myprofile.status}
            </div>
            <div>
              <strong>Mobile:</strong> {myprofile.mobile_number}
            </div>
            <div>
              <strong>Signup Date:</strong> {formatDateTime(myprofile.signup_date)}
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              requestSaveConfirmation(); // show confirm modal on enter
            }}
            className="space-y-3"
          >
            <div>
              <label className="text-sm font-medium">Username</label>
              <InputField
                name="username"
                value={form.username}
                onChange={handleChange}
                className="md:w-1/2"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Email</label>
              <InputField
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="md:w-1/2"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Mobile Number</label>
              <InputField
                name="mobile_number"
                value={form.mobile_number}
                onChange={handleChange}
                className="md:w-1/2"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Password (optional)</label>
              <InputField
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="Leave blank to keep current password"
                className="md:w-1/2"
              />
            </div>
          </form>
        )}
      </div>

      {/* Confirmation modal */}
      <Modal
        isOpen={confirmOpen}
        onClose={() => {
          if (!saving) setConfirmOpen(false);
        }}
        title="Confirm Save"
      >
        <div className="space-y-4">
          <p>Are you sure you want to save the changes to your profile?</p>

          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setConfirmOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Confirm"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
